import apiClient, { isApiError } from './apiClient';
import type { ApiAddress } from '../../utils/addressUtils';
import type { Paginated } from '../pagination';
import type {
  AdjustmentItem,
  AdjustmentKind,
  AdjustmentReasonKind,
  AdjustmentScope,
  Cart,
  CartItem,
  CheckoutResponse,
  DeliveryPoint,
  DeliveryPointsPayload,
  DownloadOrder,
  ExecutePaymentResponse,
  Order,
  OrderListRow,
  Media,
  OrderPayment,
  PaymentAcceptanceWarning,
  PaymentOption,
  PicCustomizationSelection,
  Product,
  ProductCustomizationSelection,
  ProductionClearance,
  ResolveImbalanceBody,
  ShippingOption,
  Uuid,
} from '../types';

const apiUrl = '/direct/ordersvc';

export interface AddToCartProductRef {
  id: Uuid;
  customizations?: ProductCustomizationSelection[];
  [key: string]: unknown;
}

export interface AddToCartPicRef {
  id: Uuid;
  /**
   * Per-pic customization selections (e.g. green-screen background).
   * Accepts either an array of `PicCustomizationSelection` objects (the shape
   * order-service `media_customization` expects) or a legacy free-form record.
   */
  customizations?: PicCustomizationSelection[] | Record<string, unknown>;
  [key: string]: unknown;
}

export interface AddToCartPayload {
  quantity?: number;
  album_id: Uuid;
  // The bff accepts either a product UUID, a full Product object, or a
  // {id, customizations} reference; some call sites pass each of these
  // and the backend extracts the relevant fields.
  product: Uuid | Product | AddToCartProductRef;
  pics?: Uuid[] | Media[] | AddToCartPicRef[] | null;
}

export interface RemoveFromCartPayload {
  line_item_id: Uuid;
}

export interface UpdateCartItemPayload {
  line_item_id: Uuid;
  quantity: number;
}

export interface ShippingOptionsPayload {
  address?: ApiAddress | null;
  album_ids: Uuid[] | null;
}

export interface PaymentOptionsPayload {
  album_ids: Uuid[] | null;
}

export interface SetShippingOptionPayload {
  shipping_option_id: Uuid;
  address: ApiAddress | null;
  delivery_point_id?: string | null;
  country?: string | null;
  recipient_phone?: string | null;
}

export interface SetPaymentOptionPayload {
  payment_option_id: Uuid;
  address: ApiAddress | null;
}

export interface GetAddressesPayload {
  address_type?: 'billing' | 'shipping';
}

export type ExecutePaymentArgs =
  | string
  | { paymentId: string; returnParams?: Record<string, string> };

export interface GetOrdersParams {
  page?: number;
  sort?: string;
  q?: string;
  payment_status?: string;
  production_status?: string;
  status?: string;
  clearance?: string;
  // Force user-scoping regardless of role. The customer-facing "My Orders"
  // page sends this so admins viewing /orders see only their own orders;
  // the admin orders page omits it to get the full account view.
  self_only?: boolean;
  // Filter orders by a specific user (admin user detail page).
  user_id?: string;
}

/** Response shape of `orderApi.setProductionClearance` — the subset of `Order` the endpoint returns. */
export type SetProductionClearanceResult = Pick<
  Order,
  'id' | 'status' | 'production_clearance' | 'production_cleared' | 'clearance_changed_at' | 'clearance_changed_by' | 'clearance_note'
>;

const orderApi = {
  getCart: () => apiClient.get<Cart>(`${apiUrl}/cart`),

  addToCart: ({ quantity = 1, album_id, product, pics }: AddToCartPayload) =>
    apiClient.post<CartItem>(`${apiUrl}/cart/cart_items`, {
      quantity,
      album_id,
      product,
      pics,
    }),

  removeFromCart: ({ line_item_id }: RemoveFromCartPayload) =>
    apiClient.delete<void>(`${apiUrl}/cart/cart_items/${line_item_id}`),

  updateCartItem: ({ line_item_id, quantity }: UpdateCartItemPayload) =>
    apiClient.put<CartItem>(`${apiUrl}/cart/cart_items/${line_item_id}`, { quantity }),

  getShippingOptions: ({ address, album_ids }: ShippingOptionsPayload) =>
    apiClient.post<ShippingOption[]>(`${apiUrl}/shipping_options`, { address, album_ids }),

  getDeliveryPoints: ({ shipping_option_id, country, query }: DeliveryPointsPayload) =>
    apiClient.post<{ delivery_points: DeliveryPoint[] }>(
      `${apiUrl}/shipping_options/${shipping_option_id}/delivery_points`,
      { country, query },
    ),

  getPaymentOptions: ({ album_ids }: PaymentOptionsPayload) =>
    apiClient.post<PaymentOption[]>(`${apiUrl}/payment_options`, { album_ids }),

  setShippingOption: ({ shipping_option_id, address, delivery_point_id, country, recipient_phone }: SetShippingOptionPayload) =>
    apiClient.post<Cart>(`${apiUrl}/cart/set_shipping_option`, {
      shipping_option_id,
      address,
      delivery_point_id,
      country,
      recipient_phone,
    }),

  setPaymentOption: ({ payment_option_id, address }: SetPaymentOptionPayload) =>
    apiClient.post<Cart>(`${apiUrl}/cart/set_payment_option`, {
      payment_option_id,
      address,
    }),

  getAddresses: ({ address_type = 'billing' }: GetAddressesPayload = {}) =>
    apiClient.get<ApiAddress[]>(`${apiUrl}/addresses`, { params: { address_type } }),

  checkout: (body: { terms_accepted: boolean }) =>
    apiClient.post<CheckoutResponse>(`${apiUrl}/orders/checkout`, body),

  // Backend returns the paginated `OrderListRow` envelope (a strict subset of
  // `Order` fields). Callers (Orders.tsx, AdminOrders.tsx, AdminUserDetail)
  // read `.items`.
  getOrders: (params: GetOrdersParams = {}) => {
    const query: Record<string, string> = {};
    if (params.page) query.page = String(params.page);
    if (params.sort) query.sort = params.sort;
    if (params.q) query.q = params.q;
    if (params.payment_status) query.payment_status = params.payment_status;
    if (params.production_status) query.production_status = params.production_status;
    if (params.status) query.status = params.status;
    if (params.clearance) query.clearance = params.clearance;
    if (params.self_only) query.self_only = 'true';
    if (params.user_id) query.user_id = params.user_id;
    return apiClient.get<Paginated<OrderListRow>>(`${apiUrl}/orders`, {
      params: Object.keys(query).length > 0 ? query : undefined,
    });
  },

  getOrder: (orderId: Uuid) => apiClient.get<Order>(`${apiUrl}/orders/${orderId}`),

  /**
   * Every digital purchase of the calling customer, grouped by order, newest
   * order first. Not-ready items carry a status and no files.
   */
  listDownloads: () => apiClient.get<DownloadOrder[]>(`${apiUrl}/downloads`),

  /**
   * Fetches a presigned attachment URL for a produced digital line item and
   * triggers the download. `position` addresses one file of a multi-file item;
   * omit it for the first (the order-detail page's behaviour).
   *
   * No filename argument: the anchor's `download` attribute is ignored for
   * cross-origin URLs, and a presigned storage URL always is — order-service
   * sets the saved-as name via Content-Disposition.
   */
  downloadLineItem: async (orderId: Uuid, lineItemId: Uuid, position?: number): Promise<void> => {
    const query = position === undefined ? '' : `?position=${position}`;
    const { url } = await apiClient.get<{ url: string }>(
      `${apiUrl}/orders/${orderId}/line_items/${lineItemId}/download${query}`,
    );
    const a = document.createElement('a');
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },

  /**
   * Fetches a presigned inline (streaming) URL for a produced digital VIDEO
   * line item. Long TTL server-side; the browser then plays it directly from
   * storage via Range requests — call this when the user presses Watch, not
   * on page load, so the TTL starts at watch time.
   */
  streamLineItem: (orderId: Uuid, lineItemId: Uuid, position?: number): Promise<{ url: string }> =>
    apiClient.get<{ url: string }>(
      `${apiUrl}/orders/${orderId}/line_items/${lineItemId}/stream${
        position === undefined ? '' : `?position=${position}`
      }`,
    ),

  executePayment: (args: ExecutePaymentArgs) => {
    const paymentId = typeof args === 'string' ? args : args.paymentId;
    const returnParams = typeof args === 'string' ? {} : args.returnParams || {};
    return apiClient.post<ExecutePaymentResponse>(
      `${apiUrl}/order_payments/${paymentId}/execute`,
      returnParams,
    );
  },

  cancelPayment: (paymentId: Uuid) =>
    apiClient.post<Order>(`${apiUrl}/order_payments/${paymentId}/cancel`, {}),

  /**
   * Starts or retries payment on an existing unpaid (or partially unpaid)
   * order, optionally switching payment_option_id. Recovery path for a
   * checkout whose gateway prepare failed after the order was already
   * committed (#98), and for a buyer who abandoned the gateway page.
   * Omitting paymentOptionId retries the order's current method. Returns
   * the same envelope shape as `checkout`.
   */
  startOrderPayment: (orderId: Uuid, paymentOptionId?: Uuid) =>
    apiClient.post<CheckoutResponse>(
      `${apiUrl}/orders/${orderId}/payment`,
      paymentOptionId ? { payment_option_id: paymentOptionId } : {},
    ),

  /**
   * The payment options a buyer may switch to on an existing order (#98 task
   * 13b). Serialized identically to #getPaymentOptions (POST /payment_options),
   * so it shares the same `PaymentOption[]` type — but the album list is
   * derived from the order's own line items server-side (the cart no longer
   * exists once an order is placed), hence the separate order-scoped route.
   */
  orderPaymentOptions: (orderId: Uuid) =>
    apiClient.get<PaymentOption[]>(`${apiUrl}/orders/${orderId}/payment_options`),

  /**
   * Marks a partially-settled payment as fully completed. Answers 409 with
   * acceptance warnings (see `readAcceptanceWarnings`) when the order is
   * cancelled or its shipping option has lapsed; re-send with
   * `accept_warnings` covering every returned code to proceed.
   */
  completeOrderPaymentAtFull: (
    paymentId: Uuid,
    payload: { accept_warnings?: string[] } = {},
  ) =>
    apiClient.post<Order>(
      `${apiUrl}/order_payments/${paymentId}/complete_at_full`,
      payload,
    ),

  listResolutionEligiblePaymentOptions: () =>
    apiClient.get<PaymentOption[]>(`${apiUrl}/payment_options/resolution_eligible`),

  recordOrderPayment: (
    orderId: Uuid,
    payload: {
      type: 'received' | 'refund';
      amount: number;
      amount_received: number;
      payment_option_id: Uuid;
      note?: string;
      reduce_order_total?: boolean;
      /** ISO date (YYYY-MM-DD) the money actually arrived; omit for "today". */
      settled_on?: string;
      /**
       * Acceptance-warning codes the admin has explicitly confirmed. Without
       * one entry per code the endpoint answers 409 and records nothing.
       */
      accept_warnings?: string[];
      /**
       * Settles a specific open payment row instead of matching by
       * method/amount/date. Omit to record a new payment.
       */
      order_payment_id?: Uuid;
    },
  ) =>
    apiClient.post<OrderPayment>(`${apiUrl}/orders/${orderId}/order_payments`, payload),

  resolveImbalance: (orderId: Uuid, body: ResolveImbalanceBody) =>
    apiClient.post<Order>(`${apiUrl}/orders/${orderId}/resolve_imbalance`, body),

  setProductionClearance: (orderId: Uuid, body: { clearance: ProductionClearance; note?: string }) =>
    apiClient.patch<SetProductionClearanceResult>(
      `${apiUrl}/admin/orders/${orderId}/production_clearance`,
      body,
    ),

  createOrderAdjustment: (
    orderId: Uuid,
    payload: {
      kind: AdjustmentKind;
      scope: AdjustmentScope;
      reason_kind: AdjustmentReasonKind;
      reason_text?: string;
      amount?: number | null;
      multiplier?: number | null;
      position?: number;
    },
  ) =>
    apiClient.post<AdjustmentItem>(`${apiUrl}/orders/${orderId}/adjustment_items`, payload),

  createCartAdjustment: (
    cartId: Uuid,
    payload: {
      kind: AdjustmentKind;
      scope: AdjustmentScope;
      reason_kind: AdjustmentReasonKind;
      reason_text?: string;
      amount?: number | null;
      multiplier?: number | null;
      position?: number;
    },
  ) =>
    apiClient.post<AdjustmentItem>(`${apiUrl}/carts/${cartId}/adjustment_items`, payload),

  deleteAdjustment: (adjustmentId: Uuid) =>
    apiClient.delete<void>(`${apiUrl}/adjustment_items/${adjustmentId}`),

  generateLabel: (orderId: Uuid, shipmentId: Uuid) =>
    apiClient.post<unknown>(`${apiUrl}/admin/orders/${orderId}/order_shipments/${shipmentId}/label`, {}),

  voidLabel: (orderId: Uuid, shipmentId: Uuid) =>
    apiClient.delete<unknown>(`${apiUrl}/admin/orders/${orderId}/order_shipments/${shipmentId}/label`),

  getShipmentLabelUrl: (orderId: Uuid, shipmentId: Uuid): Promise<{ url: string }> =>
    apiClient.get<{ url: string }>(`${apiUrl}/admin/orders/${orderId}/order_shipments/${shipmentId}/label`),
};

/**
 * Pulls the acceptance warnings out of a failed admin payment-acceptance call
 * (order-service `PaymentAcceptanceGate`). Returns null for anything that is
 * not a 409-with-warnings, so a truthy result means "show the confirmation
 * modal" and a falsy one means "handle this as an ordinary error".
 *
 * `ApiError` carries no typed payload for the envelope's `resp`, so the cast
 * is the documented way to read a per-endpoint body (see the
 * `ExecutePaymentResponse` note in api/types.ts).
 */
export const readAcceptanceWarnings = (
  err: unknown,
): PaymentAcceptanceWarning[] | null => {
  if (!isApiError(err) || err.status !== 409) return null;
  const resp = err.body?.resp as { warnings?: PaymentAcceptanceWarning[] } | undefined;
  const warnings = resp?.warnings;
  // A 409 from somewhere else may carry any shape (or a string `resp`), so
  // require an actual non-empty array before claiming it is the gate's.
  return Array.isArray(warnings) && warnings.length > 0 ? warnings : null;
};

export default orderApi;
